import { useQuery } from '@tanstack/react-query';
import axiosInstance, { jsonHeaders } from '@/api/axiosInstance';
import { compactApiParams } from '@/api/queryParams';
import { useUser } from '@/context/UserContext';
import { resolveRequestUserId } from '@/services/requestContext';
import type {
  Customer,
  Material,
  MaterialUnitOfMeasure,
  OrderType,
  Route,
  SalesOrg,
  ShippingCondition,
  Incoterm,
  Currency,
  NotifyPartner,
  SupplyPoint,
  AvailableRailcar,
  DrcCode,
  FreightForwarder,
  Address,
  ContainerType,
  Consignee,
  FreightPayer,
  IncotermPair,
} from '@/types';

interface PagedLookupResult<T> {
  data: T[];
  count: number;
}

function firstDefinedString(values: Array<unknown>) {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) {
        return trimmed;
      }
      continue;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }

    if (value != null) {
      const stringValue = String(value).trim();
      if (stringValue) {
        return stringValue;
      }
    }
  }

  return '';
}

function normalizeSupplyPointRow(row: SupplyPoint & Record<string, unknown>): SupplyPoint {
  const supplyPointCode = firstDefinedString([
    row.supplyPointCode,
    row.SupplyPointCode,
    row.SupplyPointPlantNumber,
    row.supplyPointPlantNumber,
    row.supplyPointId,
    row.id,
  ]);

  const description = firstDefinedString([
    row.description,
    row.Description,
    row.SupplyPointDescription,
    row.supplyPointDescription,
    row.supplyPointName,
    row.name,
  ]);

  const address = firstDefinedString([
    row.address,
    row.Address,
    row.addressLine1,
    row.AddressLine1,
  ]);

  const addressLine2 = firstDefinedString([
    row.addressLine2,
    row.AddressLine2,
  ]);

  const city = firstDefinedString([
    row.city,
    row.City,
  ]);

  const state = firstDefinedString([
    row.state,
    row.State,
    row.StateProvince,
    row.stateProvince,
    row.region,
  ]);

  const country = firstDefinedString([
    row.country,
    row.Country,
  ]);

  const zip = firstDefinedString([
    row.zip,
    row.Zip,
    row.zipCode,
    row.ZipCode,
    row.PostalZipCode,
    row.postalZipCode,
  ]);

  const supplyPointTimeZone = firstDefinedString([
    row.supplyPointTimeZone,
    row.SupplyPointTimeZone,
  ]);

  return {
    ...row,
    supplyPointId: row.supplyPointId ?? supplyPointCode,
    supplyPointName: row.supplyPointName ?? description,
    plant: row.plant ?? supplyPointCode,
    region: row.region ?? '',
    supplyPointCode,
    description,
    address,
    addressLine1: row.addressLine1 ?? address,
    addressLine2: row.addressLine2 ?? addressLine2,
    city: row.city ?? city,
    state,
    stateProvince: row.stateProvince ?? state,
    country: row.country ?? country,
    zip: row.zip ?? zip,
    zipCode: row.zipCode ?? zip,
    postalZipCode: row.postalZipCode ?? zip,
    supplyPointTimeZone: row.supplyPointTimeZone ?? supplyPointTimeZone,
  };
}

function normalizeRouteRow(row: Route & Record<string, unknown>): Route {
  const routeCode = firstDefinedString([
    row.routeCode,
    row.RouteCode,
    row.routeId,
    row.RouteId,
  ]);

  const description = firstDefinedString([
    row.description,
    row.Description,
    row.routeDescription,
  ]);

  const carrier = firstDefinedString([
    row.carrier,
    row.Carrier,
  ]);

  const modeOfTransportation = firstDefinedString([
    row.modeOfTransportation,
    row.ModeOfTransportation,
  ]);

  const originPlant = firstDefinedString([
    row.originPlant,
    row.OriginPlant,
    row.pointOfOrigin,
    row.PointOfOrigin,
  ]);

  const destinationPlant = firstDefinedString([
    row.destinationPlant,
    row.DestinationPlant,
    row.pointOfDestination,
    row.PointOfDestination,
  ]);

  const transitDaysValue = firstDefinedString([
    row.transitDays,
    row.TransitDays,
  ]);
  const leadTimeValue = firstDefinedString([
    row.leadTime,
    row.LeadTime,
  ]);
  const allowedWeightValue = firstDefinedString([
    row.allowedWeight,
    row.AllowedWeight,
  ]);

  const transitDays = transitDaysValue ? Number(transitDaysValue) : undefined;
  const leadTime = leadTimeValue ? Number(leadTimeValue) : undefined;
  const allowedWeight = allowedWeightValue ? Number(allowedWeightValue) : undefined;

  return {
    ...row,
    routeId: row.routeId ?? routeCode,
    routeCode,
    routeDescription: row.routeDescription ?? description,
    description,
    transitDays: row.transitDays ?? (Number.isFinite(transitDays) ? transitDays : undefined),
    leadTime: row.leadTime ?? (Number.isFinite(leadTime) ? leadTime : undefined),
    carrier: row.carrier ?? carrier,
    modeOfTransportation: row.modeOfTransportation ?? modeOfTransportation,
    originPlant: row.originPlant ?? originPlant,
    destinationPlant: row.destinationPlant ?? destinationPlant,
    allowedWeight: row.allowedWeight ?? (Number.isFinite(allowedWeight) ? allowedWeight : undefined),
  };
}

async function fetchLookup<T>(
  endpoint: string,
  payload: Record<string, unknown>,
): Promise<T[]> {
  const { data } = await axiosInstance.post<T[] | { data: T[] } | { data: { data: T[] } }>(
    endpoint,
    payload,
    { headers: jsonHeaders() },
  );

  if (Array.isArray(data)) {
    return data;
  }

  const firstLevel = (data as { data?: unknown }).data;
  if (Array.isArray(firstLevel)) {
    return firstLevel as T[];
  }

  const secondLevel = (firstLevel as { data?: unknown } | undefined)?.data;
  if (Array.isArray(secondLevel)) {
    return secondLevel as T[];
  }

  return [];
}

function normalizeLookupRows<T>(data: unknown): T[] {
  if (Array.isArray(data)) {
    return data as T[];
  }

  if (!data || typeof data !== 'object') {
    return [];
  }

  const record = data as { data?: unknown; datas?: unknown };

  if (Array.isArray(record.datas)) {
    return record.datas as T[];
  }

  if (Array.isArray(record.data)) {
    return record.data as T[];
  }

  if (record.data && typeof record.data === 'object') {
    const nested = record.data as { data?: unknown; datas?: unknown };

    if (Array.isArray(nested.datas)) {
      return nested.datas as T[];
    }

    if (Array.isArray(nested.data)) {
      return nested.data as T[];
    }
  }

  return [];
}

async function fetchLookupGet<T>(
  endpoint: string,
  params: Record<string, unknown> = {},
): Promise<T[]> {
  const { data } = await axiosInstance.get(endpoint, {
    params: compactApiParams(params),
    headers: jsonHeaders(),
  });

  return normalizeLookupRows<T>(data);
}

const normalizeOrderTypeRows = (rows: Array<OrderType & Record<string, unknown>>): OrderType[] => rows
  .map((row) => {
    const code = firstDefinedString([
      row.orderTypeCode,
      row.orderType,
      row.code,
      row.Code,
    ]);
    const normalizedCode = code.toUpperCase();
    const description = firstDefinedString([
      row.orderTypeDescription,
      row.description,
      row.Description,
      normalizedCode === 'ZOR' || normalizedCode === 'OR'
        ? 'Sales Order'
        : normalizedCode === 'UB' || normalizedCode === 'NB'
          ? 'Stock Order'
          : '',
    ]);

    return {
      ...row,
      orderTypeCode: code,
      orderTypeDescription: description,
    };
  })
  .filter((row) => row.orderTypeCode);

async function fetchPagedLookup<T>(
  endpoint: string,
  payload: Record<string, unknown>,
): Promise<PagedLookupResult<T>> {
  const { data } = await axiosInstance.post<
    T[]
    | { count?: number; data?: T[] }
    | { data?: { count?: number; data?: T[] } }
  >(endpoint, payload, { headers: jsonHeaders() });

  if (Array.isArray(data)) {
    return { data, count: data.length };
  }

  if (Array.isArray((data as { data?: unknown }).data)) {
    const rows = ((data as { data?: T[] }).data ?? []);
    const count = Number((data as { count?: number }).count ?? rows.length);
    return { data: rows, count };
  }

  const nested = (data as { data?: { count?: number; data?: T[] } }).data;
  return {
    data: nested?.data ?? [],
    count: Number(nested?.count ?? nested?.data?.length ?? 0),
  };
}

async function fetchPagedLookupGet<T>(
  endpoint: string,
  params: Record<string, unknown> = {},
): Promise<PagedLookupResult<T>> {
  const { data } = await axiosInstance.get(endpoint, {
    params: compactApiParams(params),
    headers: jsonHeaders(),
  });

  if (Array.isArray(data)) {
    return { data, count: data.length };
  }

  if (data && typeof data === 'object') {
    const record = data as {
      count?: number;
      data?: unknown;
      datas?: unknown;
      totalCount?: number;
    };

    if (Array.isArray(record.datas)) {
      const rows = record.datas as T[];
      return {
        data: rows,
        count: Number(record.totalCount ?? record.count ?? rows.length),
      };
    }

    if (Array.isArray(record.data)) {
      const rows = record.data as T[];
      return {
        data: rows,
        count: Number(record.count ?? record.totalCount ?? rows.length),
      };
    }

    if (record.data && typeof record.data === 'object') {
      const nested = record.data as {
        count?: number;
        data?: unknown;
        datas?: unknown;
        totalCount?: number;
      };

      if (Array.isArray(nested.datas)) {
        const rows = nested.datas as T[];
        return {
          data: rows,
          count: Number(nested.totalCount ?? nested.count ?? rows.length),
        };
      }

      if (Array.isArray(nested.data)) {
        const rows = nested.data as T[];
        return {
          data: rows,
          count: Number(nested.count ?? nested.totalCount ?? rows.length),
        };
      }
    }
  }

  return { data: [], count: 0 };
}

function buildSearchRequest(source: string, userId?: string, extras: Record<string, unknown> = {}) {
  return {
    UserId: resolveRequestUserId(userId),
    ProductType: null,
    Source: source,
    Permission: 'true',
    SearchFilters: null,
    ...extras,
  };
}

function buildAngularLookupParams(source: string, filter: string | null, extras: Record<string, unknown> = {}) {
  return {
    Source: source,
    SearchFilters: filter,
    SalesOrganization: null,
    SalesOrg: null,
    DistributionChannel: null,
    Division: null,
    PartnerType: null,
    PartnerNumber: null,
    CustomerNumber: null,
    OrderType: null,
    ShippingCondition: null,
    LinesPerPage: null,
    PageNumber: null,
    ...extras,
  };
}

export function useCustomers(searchTerm: string, enabled = true, pageNumber = 1, linesPerPage = 5) {
  const { getBusinessUnit } = useUser();
  return useQuery<PagedLookupResult<Customer>>({
    queryKey: ['lookup/customers', searchTerm, getBusinessUnit(), pageNumber, linesPerPage],
    queryFn: () => fetchPagedLookupGet<Customer>(
      '/CustomerExpanded',
      {
        SearchFilters: searchTerm,
        LinesPerPage: linesPerPage,
        PageNumber: pageNumber,
        Permission: true,
      },
    ),
    enabled,
  });
}

export function useMaterials(searchTerm: string) {
  const { getBusinessUnit } = useUser();
  return useQuery<Material[]>({
    queryKey: ['lookup/materials', searchTerm, getBusinessUnit()],
    queryFn: () => fetchLookup<Material>('/lookup/materials', { source: getBusinessUnit(), searchTerm }),
    enabled: searchTerm.length >= 2,
  });
}

interface MaterialsSTOLookupParams {
  customerNumber?: string | null;
  distributionChannel?: string | null;
  division?: string | null;
  filter?: string;
  orderType?: string | null;
  salesOrganization?: string | null;
  supplyPoint?: string | null;
  enabled?: boolean;
}

export function useMaterialsSTO(params: MaterialsSTOLookupParams = {}) {
  const { getBusinessUnit } = useUser();
  const {
    customerNumber = null,
    distributionChannel = null,
    division = null,
    filter = '',
    orderType = null,
    salesOrganization = null,
    supplyPoint = null,
    enabled = true,
  } = params;

  const normalizeLookupValue = (value: string | null | undefined) => String(value ?? '').trim();

  const normalizedFilter = normalizeLookupValue(filter).toLowerCase();
  const businessUnit = getBusinessUnit();
  const normalizedCustomerNumber = normalizeLookupValue(customerNumber);
  const normalizedDistributionChannel = normalizeLookupValue(distributionChannel);
  const normalizedDivision = normalizeLookupValue(division);
  const normalizedOrderType = normalizeLookupValue(orderType);
  const normalizedSalesOrganization = normalizeLookupValue(salesOrganization);
  const normalizedSupplyPoint = normalizeLookupValue(supplyPoint);
  
  const shouldExecute = enabled && !!businessUnit;

  return useQuery<Material[]>({
    queryKey: [
      'lookup/materialsSTO',
      businessUnit,
      normalizedCustomerNumber,
      normalizedDistributionChannel,
      normalizedDivision,
      normalizedFilter,
      normalizedOrderType,
      normalizedSalesOrganization,
      normalizedSupplyPoint,
    ],
    queryFn: async () => {
      const payload = {
        CustomerNumber: normalizedCustomerNumber,
        DistributionChannel: normalizedDistributionChannel,
        Division: normalizedDivision,
        OrderType: normalizedOrderType,
        SearchFilters: normalizedFilter,
        SalesOrganization: normalizedSalesOrganization,
        SupplyPoint: normalizedSupplyPoint,
      };

      return fetchLookupGet<Material>('/expandedmaterial', payload);
    },
    enabled: shouldExecute,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useMaterialUOMs(materialCode?: string | null, enabled = true) {
  const { getBusinessUnit } = useUser();
  const normalizedMaterialCode = String(materialCode ?? '').trim();

  return useQuery<MaterialUnitOfMeasure[]>({
    queryKey: ['lookup/materialUOMs', getBusinessUnit(), normalizedMaterialCode],
    queryFn: () => fetchLookupGet<MaterialUnitOfMeasure>('/materialUnitsOfMeasure', {
      MaterialCode: normalizedMaterialCode,
    }),
    enabled: enabled && !!getBusinessUnit() && !!normalizedMaterialCode,
  });
}

interface RoutesLookupParams {
  searchTerm?: string;
  shippingCondition?: string | null;
  shipTo?: string | null;
  supplyPoint?: string | null;
  materialCode?: string | null;
  pageNumber?: number;
  linesPerPage?: number;
  enabled?: boolean;
}

export function useRoutes(params: RoutesLookupParams = {}) {
  const { getBusinessUnit, userData } = useUser();
  const {
    searchTerm = '',
    shippingCondition = null,
    shipTo = null,
    supplyPoint = null,
    materialCode = null,
    pageNumber = 1,
    linesPerPage = 5,
    enabled = true,
  } = params;

  const normalizeOptional = (value: string | null | undefined) => {
    const normalized = String(value ?? '').trim();
    return normalized || null;
  };

  const normalizedSearchTerm = String(searchTerm ?? '').trim().toLowerCase();

  return useQuery<PagedLookupResult<Route>>({
    queryKey: ['lookup/routes', getBusinessUnit(), searchTerm, shippingCondition, shipTo, supplyPoint, materialCode, pageNumber, linesPerPage],
    queryFn: async () => {
      const result = await fetchPagedLookupGet<Route & Record<string, unknown>>(
        '/Route',
        {
          SearchFilters: normalizedSearchTerm,
          LinesPerPage: linesPerPage,
          PageNumber: pageNumber,
          ShippingCondition: normalizeOptional(shippingCondition),
          ShipTo: normalizeOptional(shipTo),
          SupplyPoint: normalizeOptional(supplyPoint),
          CustomerNumber: null,
          SalesOrg: null,
          DistributionChannel: null,
          Division: null,
          ShowQi: false,
          SortingColumn: null,
          SortingDirection: null,
          Permission: true,
          'Permission.ValueKind': 'True',
        },
      );

      return {
        count: result.count,
        data: result.data.map((row) => normalizeRouteRow(row)),
      };
    },
    enabled: enabled && !!getBusinessUnit(),
  });
}

export function useSalesOrgs() {
  const { getBusinessUnit } = useUser();
  return useQuery<SalesOrg[]>({
    queryKey: ['lookup/salesOrgs', getBusinessUnit()],
    queryFn: () => fetchLookup<SalesOrg>('/lookup/salesOrgs', { source: getBusinessUnit() }),
  });
}

export function useShippingConditions() {
  const { getBusinessUnit } = useUser();
  return useQuery<ShippingCondition[]>({
    queryKey: ['lookup/shippingConditions', getBusinessUnit()],
    queryFn: () => fetchLookupGet<ShippingCondition>('/ShippingCondition'),
    enabled: !!getBusinessUnit(),
  });
}

export function useIncoterms() {
  const { getBusinessUnit } = useUser();
  return useQuery<Incoterm[]>({
    queryKey: ['lookup/incoterms', getBusinessUnit()],
    queryFn: async () => {
      const result = await fetchPagedLookupGet<Incoterm>('/incoterms', {
        PageNumber: 1,
        PageSize: 250,
        Source: getBusinessUnit(),
      });

      return result.data;
    },
    enabled: !!getBusinessUnit(),
  });
}

export function useContainerTypes() {
  const { getBusinessUnit } = useUser();
  return useQuery<ContainerType[]>({
    queryKey: ['lookup/containerTypes', getBusinessUnit()],
    queryFn: () => fetchLookupGet<ContainerType>('/containertypes'),
  });
}

export function useCurrencies() {
  const { getBusinessUnit } = useUser();
  const businessUnit = getBusinessUnit();
  return useQuery<Currency[]>({
    queryKey: ['lookup/currencies', businessUnit],
    queryFn: () => fetchLookupGet<Currency>('/Currency'),
    enabled: !!businessUnit,
  });
}

interface NotifyPartnersLookupParams {
  searchTerm?: string;
  customerNumber?: string | null;
  salesOrg?: string | null;
  distributionChannel?: string | null;
  division?: string | null;
  linesPerPage?: number;
  pageNumber?: number;
  enabled?: boolean;
}

export function useNotifyPartners(params: NotifyPartnersLookupParams = {}) {
  const { getBusinessUnit } = useUser();
  const {
    searchTerm = '',
    customerNumber = null,
    salesOrg = null,
    distributionChannel = null,
    division = null,
    linesPerPage = 5,
    pageNumber = 1,
    enabled = true,
  } = params;

  const normalizeOptional = (value: string | null | undefined) => {
    const normalized = String(value ?? '').trim();
    return normalized || null;
  };

  const normalizedSearchTerm = String(searchTerm ?? '').trim();
  const normalizedCustomerNumber = normalizeOptional(customerNumber);
  const normalizedSalesOrg = normalizeOptional(salesOrg);
  const normalizedDistributionChannel = normalizeOptional(distributionChannel);
  const normalizedDivision = normalizeOptional(division);

  return useQuery<PagedLookupResult<NotifyPartner>>({
    queryKey: [
      'lookup/notifyPartners',
      getBusinessUnit(),
      normalizedSearchTerm,
      normalizedCustomerNumber,
      normalizedSalesOrg,
      normalizedDistributionChannel,
      normalizedDivision,
      linesPerPage,
      pageNumber,
    ],
    queryFn: () => fetchPagedLookupGet<NotifyPartner>(
      '/notifypartners',
      {
        CustomerNumber: normalizedCustomerNumber,
        SalesOrganization: normalizedSalesOrg,
        DistributionChannel: normalizedDistributionChannel,
        Division: normalizedDivision,
        LinesPerPage: linesPerPage,
        PageNumber: pageNumber,
        SearchFilters: normalizedSearchTerm,
      },
    ),
    enabled: enabled
      && !!getBusinessUnit()
      && !!normalizedCustomerNumber
      && !!normalizedSalesOrg
      && !!normalizedDistributionChannel
      && !!normalizedDivision,
  });
}

interface ConsigneesLookupParams {
  searchTerm?: string;
  customerNumber?: string | null;
  salesOrg?: string | null;
  distributionChannel?: string | null;
  division?: string | null;
  linesPerPage?: number;
  pageNumber?: number;
  enabled?: boolean;
}

export function useConsignees(params: ConsigneesLookupParams = {}) {
  const { getBusinessUnit } = useUser();
  const {
    searchTerm = '',
    customerNumber = null,
    salesOrg = null,
    distributionChannel = null,
    division = null,
    linesPerPage = 5,
    pageNumber = 1,
    enabled = true,
  } = params;

  const normalizeOptional = (value: string | null | undefined) => {
    const normalized = String(value ?? '').trim();
    return normalized || null;
  };

  const normalizedSearchTerm = String(searchTerm ?? '').trim();
  const normalizedCustomerNumber = normalizeOptional(customerNumber);
  const normalizedSalesOrg = normalizeOptional(salesOrg);
  const normalizedDistributionChannel = normalizeOptional(distributionChannel);
  const normalizedDivision = normalizeOptional(division);

  return useQuery<PagedLookupResult<Consignee>>({
    queryKey: [
      'lookup/consignees',
      getBusinessUnit(),
      normalizedSearchTerm,
      normalizedCustomerNumber,
      normalizedSalesOrg,
      normalizedDistributionChannel,
      normalizedDivision,
      linesPerPage,
      pageNumber,
    ],
    queryFn: () => fetchPagedLookupGet<Consignee>(
      '/consignee',
      {
        CustomerNumber: normalizedCustomerNumber,
        SalesOrganization: normalizedSalesOrg,
        DistributionChannel: normalizedDistributionChannel,
        Division: normalizedDivision,
        ConsigneeNumber: normalizedSearchTerm,
        Name: normalizedSearchTerm,
        LinesPerPage: linesPerPage,
        PageNumber: pageNumber,
      },
    ),
    enabled: enabled
      && !!getBusinessUnit()
      && !!normalizedCustomerNumber
      && !!normalizedSalesOrg
      && !!normalizedDistributionChannel
      && !!normalizedDivision,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

interface IncotermPairsLookupParams {
  salesOrg?: string | null;
  distributionChannel?: string | null;
  division?: string | null;
  customerNumber?: string | null;
  enabled?: boolean;
}

export function useIncotermPairs(params: IncotermPairsLookupParams = {}) {
  const { getBusinessUnit } = useUser();
  const {
    salesOrg = null,
    distributionChannel = null,
    division = null,
    customerNumber = null,
    enabled = true,
  } = params;

  const normalizeOptional = (value: string | null | undefined) => {
    const normalized = String(value ?? '').trim();
    return normalized || null;
  };

  const normalizedSalesOrg = normalizeOptional(salesOrg);
  const normalizedDistributionChannel = normalizeOptional(distributionChannel);
  const normalizedDivision = normalizeOptional(division);
  const normalizedCustomerNumber = normalizeOptional(customerNumber);

  return useQuery<IncotermPair[]>({
    queryKey: [
      'lookup/incotermPairs',
      getBusinessUnit(),
      normalizedSalesOrg,
      normalizedDistributionChannel,
      normalizedDivision,
      normalizedCustomerNumber,
    ],
    queryFn: async () => {
      const result = await fetchPagedLookupGet<IncotermPair>(
        '/incotermpairs',
        {
          PageNumber: 1,
          PageSize: 100,
          CustomerNo: normalizedCustomerNumber,
          CustomerName: normalizedCustomerNumber,
        SalesOrganization: normalizedSalesOrg,
        DistributionChannel: normalizedDistributionChannel,
        Division: normalizedDivision,
        },
      );

      return result.data;
    },
    enabled: enabled
      && !!getBusinessUnit()
      && !!normalizedCustomerNumber
      && !!normalizedSalesOrg
      && !!normalizedDistributionChannel
      && !!normalizedDivision,
  });
}

interface FreightPayersLookupParams {
  customerNumber?: string | null;
  salesOrg?: string | null;
  distributionChannel?: string | null;
  division?: string | null;
  enabled?: boolean;
}

export function useFreightPayers(params: FreightPayersLookupParams = {}) {
  const { getBusinessUnit } = useUser();
  const {
    customerNumber = null,
    salesOrg = null,
    distributionChannel = null,
    division = null,
    enabled = true,
  } = params;

  const normalizeOptional = (value: string | null | undefined) => {
    const normalized = String(value ?? '').trim();
    return normalized || null;
  };

  const normalizedCustomerNumber = normalizeOptional(customerNumber);
  const normalizedSalesOrg = normalizeOptional(salesOrg);
  const normalizedDistributionChannel = normalizeOptional(distributionChannel);
  const normalizedDivision = normalizeOptional(division);

  return useQuery<FreightPayer[]>({
    queryKey: [
      'lookup/freightPayers',
      getBusinessUnit(),
      normalizedCustomerNumber,
      normalizedSalesOrg,
      normalizedDistributionChannel,
      normalizedDivision,
    ],
    queryFn: () => fetchLookupGet<FreightPayer>(
      '/freightpayers',
      {
        CustomerNumber: normalizedCustomerNumber,
        SalesOrganization: normalizedSalesOrg,
        DistributionChannel: normalizedDistributionChannel,
        Division: normalizedDivision,
      },
    ),
    enabled: enabled
      && !!getBusinessUnit()
      && !!normalizedCustomerNumber
      && !!normalizedSalesOrg
      && !!normalizedDistributionChannel
      && !!normalizedDivision,
  });
}

interface SupplyPointsLookupParams {
  searchTerm?: string;
  shippingCondition?: string | null;
  pageNumber?: number;
  linesPerPage?: number;
  enabled?: boolean;
}

export function useSupplyPoints(params: SupplyPointsLookupParams = {}) {
  const { getBusinessUnit } = useUser();
  const {
    searchTerm = '',
    shippingCondition = null,
    pageNumber = 1,
    linesPerPage = 5,
    enabled = true,
  } = params;

  const normalizeOptional = (value: string | null | undefined) => {
    const normalized = String(value ?? '').trim();
    return normalized || null;
  };

  const normalizedSearchTerm = String(searchTerm ?? '').trim();

  return useQuery<PagedLookupResult<SupplyPoint>>({
    queryKey: ['lookup/supplyPoints', getBusinessUnit(), searchTerm, shippingCondition, pageNumber, linesPerPage],
    queryFn: async () => {
      const result = await fetchPagedLookupGet<SupplyPoint & Record<string, unknown>>(
        '/expandedsupplypoint',
        {
          SearchFilters: normalizedSearchTerm,
          PageNumber: pageNumber,
          LinesPerPage: linesPerPage,
          ShippingCondition: normalizeOptional(shippingCondition),
          Permission: true,
        },
      );

      return {
        count: result.count,
        data: result.data.map((row) => normalizeSupplyPointRow(row)),
      };
    },
    enabled: enabled && !!getBusinessUnit(),
  });
}

export function useAvailableRailcars(plantCode?: string) {
  return useQuery<AvailableRailcar[]>({
    queryKey: ['lookup/availableRailcars', plantCode],
    queryFn: () => fetchLookup<AvailableRailcar>('/lookup/availableRailcars', { plantCode }),
    enabled: !!plantCode,
  });
}

interface DrcCodesLookupParams {
  searchTerm?: string;
  customerNumber?: string | null;
  salesOrg?: string | null;
  distributionChannel?: string | null;
  division?: string | null;
  orderType?: string | null;
  shippingCondition?: string | null;
  pageNumber?: number;
  linesPerPage?: number;
  enabled?: boolean;
}

export function useDrcCodes(params: DrcCodesLookupParams = {}) {
  const { getBusinessUnit } = useUser();
  const {
    searchTerm = '',
    customerNumber = null,
    salesOrg = null,
    distributionChannel = null,
    division = null,
    orderType = null,
    shippingCondition = null,
    pageNumber = 1,
    linesPerPage = 5,
    enabled = true,
  } = params;

  const normalizeOptional = (value: string | null | undefined) => {
    const normalized = String(value ?? '').trim();
    return normalized || null;
  };

  const normalizedSearchTerm = String(searchTerm ?? '').trim();
  const normalizedCustomerNumber = normalizeOptional(customerNumber);
  const normalizedSalesOrg = normalizeOptional(salesOrg);
  const normalizedDistributionChannel = normalizeOptional(distributionChannel);
  const normalizedDivision = normalizeOptional(division);
  const normalizedOrderType = normalizeOptional(orderType);
  const normalizedShippingCondition = normalizeOptional(shippingCondition);

  return useQuery<PagedLookupResult<DrcCode>>({
    queryKey: [
      'lookup/drcCodes',
      getBusinessUnit(),
      searchTerm,
      normalizedCustomerNumber,
      normalizedSalesOrg,
      normalizedDistributionChannel,
      normalizedDivision,
      normalizedOrderType,
      normalizedShippingCondition,
      pageNumber,
      linesPerPage,
    ],
    queryFn: () => fetchPagedLookupGet<DrcCode>(
      '/drccodes',
      {
        SearchFilters: normalizedSearchTerm,
        LinesPerPage: linesPerPage,
        PageNumber: pageNumber,
      },
    ),
    enabled: enabled && !!getBusinessUnit(),
  });
}

interface FreightForwardersLookupParams {
  division?: string | null;
  orderType?: string | null;
  shippingCondition?: string | null;
  supplyPoint?: string | null;
  sourcePlant?: string | null;
  originCountry?: string | null;
  destinationCountry?: string | null;
  pageNumber?: number;
  linesPerPage?: number;
  enabled?: boolean;
}

export function useFreightForwarders(params: FreightForwardersLookupParams = {}) {
  const { getBusinessUnit } = useUser();
  const {
    division = null,
    orderType = null,
    shippingCondition = null,
    supplyPoint = null,
    sourcePlant = null,
    originCountry = null,
    destinationCountry = null,
    pageNumber = 1,
    linesPerPage = 5,
    enabled = true,
  } = params;

  const normalizeOptional = (value: string | null | undefined) => {
    const normalized = String(value ?? '').trim();
    return normalized || null;
  };

  const normalizedDivision = normalizeOptional(division);
  const normalizedOrderType = normalizeOptional(orderType);
  const normalizedShippingCondition = normalizeOptional(shippingCondition);
  const normalizedSupplyPoint = normalizeOptional(supplyPoint);
  const normalizedSourcePlant = normalizeOptional(sourcePlant);
  const normalizedOriginCountry = normalizeOptional(originCountry);
  const normalizedDestinationCountry = normalizeOptional(destinationCountry);

  return useQuery<PagedLookupResult<FreightForwarder>>({
    queryKey: [
      'lookup/freightForwarders',
      getBusinessUnit(),
      normalizedDivision,
      normalizedOrderType,
      normalizedShippingCondition,
      normalizedSupplyPoint,
      normalizedSourcePlant,
      normalizedOriginCountry,
      normalizedDestinationCountry,
      pageNumber,
      linesPerPage,
    ],
    queryFn: () => fetchPagedLookupGet<FreightForwarder>(
      '/freightforwarders',
      {
        PageNumber: pageNumber,
        LinesPerPage: linesPerPage,
        ShippingCondition: normalizedShippingCondition,
        Division: normalizedDivision,
        ReceivingPlant: normalizedSupplyPoint,
        SourcePlant: normalizedSourcePlant,
        OriginCountry: normalizedOriginCountry,
        DestinationCountry: normalizedDestinationCountry,
        OrderType: normalizedOrderType,
      },
    ),
    enabled: enabled && !!getBusinessUnit(),
  });
}

export function useAddressLookup(searchTerm: string) {
  return useQuery<Address[]>({
    queryKey: ['lookup/addresses', searchTerm],
    queryFn: () => fetchLookup<Address>('/lookup/addresses', { searchTerm }),
    enabled: searchTerm.length >= 3,
  });
}

export function useOrderStatuses() {
  return useQuery<string[]>({
    queryKey: ['lookup/orderStatuses'],
    queryFn: async () => {
      const { data } = await axiosInstance.get<Array<{ orderStatus: string }>>('/cyberorderstatus', {
        headers: jsonHeaders(),
      });
      return data.map((s) => s.orderStatus);
    },
  });
}

export function useOrderTypes() {
  const { getBusinessUnit } = useUser();
  return useQuery<OrderType[]>({
    queryKey: ['lookup/orderTypes', getBusinessUnit()],
    queryFn: async () => normalizeOrderTypeRows(
      await fetchLookupGet<OrderType>('/orderTypes') as Array<OrderType & Record<string, unknown>>,
    ),
  });
}

export function useLookupFetch() {
  return {
    fetchCustomers: (searchTerm: string, source: string) =>
      fetchPagedLookupGet<Customer>(
        '/CustomerExpanded',
        {
          SearchFilters: searchTerm,
          LinesPerPage: 5,
          PageNumber: 1,
          Permission: true,
        },
      ),
    fetchMaterials: (searchTerm: string, source: string) =>
      fetchLookup<Material>('/lookup/materials', { searchTerm, source }),
  };
}
